import React, { useMemo, forwardRef } from "react";
import {
  AsYouType,
  parseIncompletePhoneNumber,
  isPossiblePhoneNumber,
  CountryCode,
} from "libphonenumber-js";
import { CpInput } from "@components";
import type { CpInputProps } from "../cp-input.component";
import {
  useController,
  Control,
  RegisterOptions,
  UseControllerProps,
} from "react-hook-form";
import { mergeRefs } from "@helpers";

type PhoneNumberOptions = {
  defaultCountry?: CountryCode;
};

type CpInputFieldPhoneNumberProps = Omit<
  CpInputProps,
  "value" | "onChange" | "onBlur" | "error"
> & {
  control: Control<any>;
  controllerOptions?: Omit<UseControllerProps, "name" | "control" | "rules">;
  fieldName: string;
  rules?: RegisterOptions;
  required?: boolean;
  options?: PhoneNumberOptions;
};

export const CpInputFieldPhoneNumber = forwardRef<
  HTMLInputElement,
  CpInputFieldPhoneNumberProps
>(function CpInputFieldPhoneNumber(props, ref) {
  const {
    control,
    controllerOptions,
    fieldName,
    rules = {},
    required,
    options = {},
    ...inputProps
  } = props;
  const { defaultCountry = "US" } = options;

  const mergedRules: RegisterOptions = {
    ...rules,
    validate: {
      ...rules?.validate,
      isValidPhone: (v: string) => {
        const message = "Field must be valid phone number";
        if (!v && !required) {
          return true;
        } else if (typeof v === "string" && v.length < 3) {
          return message;
        }
        const asYouType = new AsYouType(defaultCountry);
        const formatted = asYouType.input(v);
        const phoneNumber = asYouType.getNumber();
        const isPossible = isPossiblePhoneNumber(
          phoneNumber?.number || formatted,
        );
        return isPossible || message;
      },
    },
  };
  if (required) {
    mergedRules.required = "Field is required";
  }

  const {
    field: { value = "", onChange, onBlur, ref: fieldRef },
    fieldState: { error },
  } = useController({
    control,
    name: fieldName,
    rules: mergedRules,
    ...controllerOptions,
  });

  const formattedPhone = useMemo(() => {
    return new AsYouType(defaultCountry).input(value);
  }, [defaultCountry, value]);

  function updatePhone(val: string) {
    if (val.length > 20) return;

    let newValue = parseIncompletePhoneNumber(val);
    if (val.length < formattedPhone.length) {
      if (newValue.indexOf(value) === 0) {
        newValue = newValue.slice(0, -1);
      }
    }

    onChange(newValue);
  }

  return (
    <CpInput
      value={formattedPhone}
      onChange={updatePhone}
      onBlur={onBlur}
      error={error?.message}
      required={required}
      ref={mergeRefs(ref, fieldRef)}
      {...inputProps}
    />
  );
});
